import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NoContent, ReactTable } from '../../../components/shared';
import LoadingSpinner from '../../../components/LoadingSpinner';
import SearchBar from '../../../components/SearchBar';
import NewFilterDrawer from '../../../components/shared/FilterDrawer/NewFilterDrawer';
import PostingTabToolbar from '../../../components/shared/PostingTabToolbar';
import FILTER_CONSTANTS from '../../../constants/FilterConstants';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../../constants/pagination';
import { LIST_LAYOUT, WEEKLY_GL_POSTING_TEXT } from '../constants';
import { buildWeeklyGLErrorMessage } from '../utils/glPostingMethods';
import useStyles from '../style';

const { DRAWER_TYPES, TITLE } = FILTER_CONSTANTS;
const TABLE_PAGE_INDEX_OFFSET = 1;
const DEFAULT_ERROR_TITLE = 'Unable to load data';
const DEFAULT_ERROR_DESCRIPTION = 'Please refresh or try again in a moment.';

const PostingTabContent = ({
  columns,
  filterSelector,
  setFilters,
  resetFilters,
  emptyStateTitle,
  searchPlaceholder,
  initialFilterParams,
  emptyFilterLength,
  useTabHook,
  hookArgs = {},
  noResultsText = WEEKLY_GL_POSTING_TEXT.NO_RESULTS,
}) => {
  const classes = useStyles();
  const filtersFromRedux = useSelector(filterSelector);
  const [filterParams, setFilterParams] = useState(initialFilterParams);

  const {
    filters,
    filterData,
    tableData,
    totalCount,
    isLoading,
    isFetching,
    isError,
    error,
    handlePageChange,
    handlePerPageChange,
    handleSearch,
    handleResetSearch,
    handleApplyFilter,
    handleResetFilter,
    handleRowClick,
    handleSort,
    sortState,
  } = useTabHook({
    ...hookArgs,
    filters: filtersFromRedux,
    setFilters,
    resetFilters,
  });

  useEffect(() => {
    const filterOnlyParams = Object.fromEntries(
      Object.keys(initialFilterParams).map((key) => [key, filters[key] ?? initialFilterParams[key]]),
    );
    setFilterParams(filterOnlyParams);
  }, [filters, initialFilterParams]);

  const onApplyFilterHandler = (values) => {
    handleApplyFilter(values);
    setFilterParams(values);
  };

  const onResetFilterHandler = () => {
    setFilterParams(initialFilterParams);
    handleResetFilter();
  };

  const modifiedTableData = useMemo(
    () => tableData?.map((row) => ({ ...row, onClick: () => handleRowClick(row) })) || [],
    [tableData, handleRowClick],
  );

  const showLoading = isLoading || isFetching;
  const hasTableData = Array.isArray(tableData) && tableData.length > 0;
  const shouldShowEmptyState = !showLoading && !isError && !hasTableData;
  const backendErrorText = buildWeeklyGLErrorMessage(error);
  const backendErrorLines = backendErrorText.split('\n').filter(Boolean);
  const backendErrorTitle = backendErrorLines[0] || '';
  const backendErrorDescription = backendErrorLines.slice(1).join('\n');
  const hasBackendErrorContent = Boolean(backendErrorTitle || backendErrorDescription);
  const noContentTitle = isError ? backendErrorTitle || DEFAULT_ERROR_TITLE : emptyStateTitle;
  const noContentDescription = isError ? (
    backendErrorDescription || (hasBackendErrorContent ? '' : DEFAULT_ERROR_DESCRIPTION)
  ) : (
    <>{noResultsText}</>
  );

  return (
    <>
      <PostingTabToolbar
        searchBar={
          <SearchBar
            onSearch={handleSearch}
            placeholder={searchPlaceholder}
            onReset={handleResetSearch}
            searchText={filters?.searchText || ''}
          />
        }
        filters={
          <NewFilterDrawer
            filteredValues={filterParams}
            name={TITLE.FILTERS.toUpperCase()}
            title={TITLE.FILTERS}
            drawerType={DRAWER_TYPES.FILTER_DRAWER}
            onReset={onResetFilterHandler}
            onApplyFilter={onApplyFilterHandler}
            filterData={filterData}
            emptyFilterLength={emptyFilterLength}
          />
        }
      />

      {showLoading && <LoadingSpinner containerHeight={LIST_LAYOUT.LOADING_SPINNER_HEIGHT} />}
      {(isError || shouldShowEmptyState) && <NoContent title={noContentTitle} desc={noContentDescription} />}

      {!showLoading && !isError && hasTableData && (
        <ReactTable
          data={modifiedTableData}
          columns={columns}
          pagination
          manualPagination
          totalCount={totalCount || 0}
          pageNumberForTablePagination={(filters?.pageNumber || DEFAULT_PAGE) - TABLE_PAGE_INDEX_OFFSET}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          perPage={filters?.rowsPerPage || DEFAULT_PER_PAGE}
          enableRowClick
          rowClassName={() => classes.clickableRow}
          sortable={Boolean(handleSort)}
          onSortChange={handleSort}
          sortBy={sortState}
        />
      )}
    </>
  );
};

export default PostingTabContent;
